import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  const sections = [
    {
      title: 'Introduction',
      content: `At Mild-Ads, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use the Velis Browser and related services. Please read this privacy policy carefully.`,
    },
    {
      title: 'Information We Collect',
      content: `We collect information that you provide directly to us, including:

• Account information (email address, username) when you create an account
• Browser preferences and settings you choose to sync
• Feedback and correspondence when you contact us
• Usage data to improve browser performance`,
    },
    {
      title: 'How We Use Your Information',
      content: `We use the information we collect to:

• Provide, maintain, and improve our services
• Sync your preferences across devices when enabled
• Send you technical notices and support messages
• Respond to your comments and questions
• Protect against fraudulent or illegal activity`,
    },
    {
      title: 'Data Security',
      content: `We implement appropriate technical and organizational security measures designed to protect your personal information. All data transmitted between your device and our servers is encrypted using industry-standard protocols. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.`,
    },
    {
      title: 'Third-Party Services',
      content: `Velis Browser is designed to block third-party trackers by default. We do not sell your personal information to third parties. Any third-party services integrated into our browser are clearly disclosed and require your explicit consent.`,
    },
    {
      title: 'Your Rights',
      content: `You have the right to:

• Access the personal information we hold about you
• Request correction of inaccurate data
• Request deletion of your personal information
• Opt out of marketing communications
• Export your data in a portable format`,
    },
    {
      title: 'Cookies and Tracking',
      content: `Velis Browser gives you full control over cookies and tracking. Our browser blocks third-party cookies by default and provides transparent controls for managing site permissions. We use minimal first-party cookies only for essential functionality.`,
    },
    {
      title: "Children's Privacy",
      content: `Our services are not directed to children under 13. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us immediately.`,
    },
    {
      title: 'Changes to This Policy',
      content: `We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.`,
    },
    {
      title: 'Contact Us',
      content: `If you have any questions about this Privacy Policy, please contact us at contact@velis-browser.com

Mild-Ads LTD
Knaan 5 Street, Or Yehuda, 6041923, Israel`,
    },
  ];

  return (
    <div className="min-h-screen bg-[#0f1923] text-[#e2e8f0] font-sans">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-[#3b82f6]/10 rounded-full blur-[160px]" />
        <div className="absolute bottom-1/4 left-1/4 w-[400px] h-[400px] bg-[#1d4ed8]/10 rounded-full blur-[160px]" />
      </div>

      <nav className="relative z-50 px-6 py-5 border-b border-white/5 backdrop-blur-md bg-[#0f1923]/70">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-11 h-11 bg-gradient-to-br from-[#3b82f6] to-[#1d4ed8] rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold tracking-tight">Velis Browser</span>
          </Link>
          <Link to="/" className="text-[#94a3b8] hover:text-white transition-colors font-medium flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </nav>

      <header className="relative z-10 py-16 px-6 border-b border-white/5">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-4 mb-6"
          >
            <div className="p-4 bg-gradient-to-br from-[#3b82f6]/20 to-[#1d4ed8]/20 rounded-2xl border border-white/10">
              <Shield className="w-8 h-8 text-[#60a5fa]" />
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl font-bold">Privacy Policy</h1>
              <p className="text-[#94a3b8] mt-2">Last updated: April 15, 2026</p>
            </div>
          </motion.div>
        </div>
      </header>

      <main className="relative z-10 py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="space-y-8"
          >
            {sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ delay: index * 0.04 }}
                className="bg-[#1a2735]/60 rounded-2xl border border-white/5 p-8"
              >
                <h2 className="text-2xl font-bold mb-4 text-white">{section.title}</h2>
                <div className="text-[#cbd5e1] leading-relaxed whitespace-pre-line">
                  {section.content}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </main>

      <footer className="relative z-10 py-12 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-[#3b82f6] to-[#1d4ed8] rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-bold">Velis Browser</span>
          </div>
          <div className="flex gap-8 text-sm text-[#94a3b8]">
            <Link to="/privacy" className="text-white">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms of Use</Link>
            <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
          </div>
          <p className="text-[#94a3b8]/70 text-sm">© {new Date().getFullYear()} Mild-Ads LTD</p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
